import BpkCard from '@skyscanner-internal/backpack-web/bpk-component-card';
import BpkImage, {
  withLazyLoading,
  withLoadingBehavior,
} from '@skyscanner-internal/backpack-web/bpk-component-image';
import {
  BACKGROUND_COLORS,
  BpkBox,
  BpkBreakpoint,
  BpkFlex,
  BpkSpacing,
  BpkStack,
  BpkVessel,
} from '@skyscanner-internal/backpack-web/bpk-component-layout';
import BpkLoadingButton from '@skyscanner-internal/backpack-web/bpk-component-loading-button';
import BpkText, {
  TEXT_STYLES,
  TEXT_COLORS,
} from '@skyscanner-internal/backpack-web/bpk-component-text';

import { useMBDContext } from '../common/MBD/useMBDContext';

import SearchHotelsLogo from './search-hotels.svg';

import type { HotelSearchResultsWrapperClientStrings } from '@skyscanner-internal/landing-pages-shared-types/types-shared/HotelSearchResultsWrapperProps';

import STYLES from './SeoBannerSearchHotels.module.scss';

type Props = {
  dayViewUrl: string;
  fromSeoPage?: string;
  clientStrings: HotelSearchResultsWrapperClientStrings;
};

const documentIfExists = typeof window !== 'undefined' ? document : null;
const LazyLoadedImage = withLoadingBehavior(
  withLazyLoading(BpkImage, documentIfExists),
);

const SeoBannerSearchHotels = ({
  clientStrings,
  dayViewUrl,
  fromSeoPage = undefined,
}: Props) => {
  const { buildMBDParam, rawComponentName } = useMBDContext();
  const mbdComponentName = `${rawComponentName}_seo_banner`;
  const ctaComponentName = `${mbdComponentName}_cta`;
  const element = 'seo-banner-search-hotels';
  const bannerSubtitle =
    fromSeoPage === 'City'
      ? clientStrings.seoBannerSubtitleCity
      : clientStrings.seoBannerSearchHotelsCopy;

  return (
    // `border-radius` stays on this BpkVessel's className: it is typed
    // `never` on every Backpack layout primitive. Layout (display/width/
    // height/margin/background-color) moves to the nested BpkFlex below.
    <BpkVessel
      className={STYLES.SeoBannerSearchHotels}
      data-tracking-element-id={mbdComponentName}
      data-tracking-common-params={buildMBDParam(mbdComponentName)}
    >
      <BpkFlex
        width="100%"
        height={{
          base: '15rem',
          [BpkBreakpoint.SmallTablet]: '10.5rem',
        }}
        marginTop={BpkSpacing.Base}
        marginBottom={BpkSpacing.Base}
        backgroundColor={BACKGROUND_COLORS.surfaceDefault}
      >
        <BpkCard className={STYLES.card} padded={false} atomic={false}>
          <BpkStack
            direction={{
              base: 'column',
              [BpkBreakpoint.SmallTablet]: 'row',
            }}
            align="stretch"
            gap={BpkSpacing.Base}
            width="100%"
            height={{
              base: '15rem',
              [BpkBreakpoint.SmallTablet]: '100%',
            }}
            paddingBlock={{
              base: BpkSpacing.Base,
              [BpkBreakpoint.SmallTablet]: BpkSpacing.None,
            }}
            paddingInline={{
              base: BpkSpacing.Base,
              [BpkBreakpoint.SmallTablet]: BpkSpacing.LG,
            }}
          >
            <BpkBox
              height="100%"
              display={{
                base: 'none',
                [BpkBreakpoint.SmallTablet]: 'block',
              }}
            >
              {/*
                `transform: scaleX(-1)` stays on this BpkVessel's className:
                it only applies inside the `bpk-rtl` mixin ([dir="rtl"]
                scoped). BpkBox's `transform` prop (the only layout
                primitive that exposes `transform` at all) is a static,
                unconditional value - using it here would mirror the logo
                in LTR too. See
                WOM-6807-seobannersearchhotels-gaps.md Gap 1.
              */}
              <BpkVessel className={STYLES.SeoBannerSearchHotels__logo}>
                <BpkBox
                  width="12.6875rem"
                  height="100%"
                  paddingTop={BpkSpacing.Base}
                >
                  <LazyLoadedImage
                    alt={element}
                    src={SearchHotelsLogo}
                    aspectRatio={203 / 152}
                    role="presentation"
                  />
                </BpkBox>
              </BpkVessel>
            </BpkBox>
            <BpkVessel className={STYLES.SeoBannerSearchHotels__middle}>
              <BpkBox
                height="100%"
                overflow="hidden"
                paddingBlock={{
                  base: BpkSpacing.None,
                  [BpkBreakpoint.SmallTablet]: BpkSpacing.Base,
                }}
                paddingInline={BpkSpacing.None}
              >
                <BpkBox paddingBottom={BpkSpacing.Base}>
                  <BpkText
                    tagName="p"
                    textStyle={TEXT_STYLES.label1}
                    color={TEXT_COLORS.textPrimary}
                  >
                    {clientStrings.seoBannerSearchHotelsTitle}
                  </BpkText>
                </BpkBox>
                <BpkBox>
                  <BpkText tagName="p" textStyle={TEXT_STYLES.bodyDefault}>
                    {bannerSubtitle}
                  </BpkText>
                </BpkBox>
              </BpkBox>
            </BpkVessel>
            <BpkBox marginBlock="auto" flexShrink={0}>
              <div className={STYLES.SeoBannerSearchHotels__btn}>
                <BpkLoadingButton
                  target="_blank"
                  rel="nofollow"
                  href={dayViewUrl}
                  data-tracking-element-id={ctaComponentName}
                  data-tracking-common-params={buildMBDParam(ctaComponentName)}
                >
                  {clientStrings.seoBannerSearchCTAText}
                </BpkLoadingButton>
              </div>
            </BpkBox>
          </BpkStack>
        </BpkCard>
      </BpkFlex>
    </BpkVessel>
  );
};

export default SeoBannerSearchHotels;
